import { faBadgeDollar, faEdit } from '@fortawesome/pro-regular-svg-icons'
import { faCircleQuestion } from '@fortawesome/pro-regular-svg-icons/faCircleQuestion'
import { Box, Button, Flex, Radio, Switch, Text, Tooltip } from '@mantine/core'
import { isBefore } from 'date-fns'
import { useEffect, useState } from 'react'

import {
  useMutationUpdateBooking,
  useQueryGetBookingById,
  useQueryGetAllBookingInsurancesQuote,
} from '@flecto/core/saas'
import {
  useQueryGetUserAddresses,
  useQueryGetUserLogged,
} from '@flecto/core/shared'
import { useTranslation } from '@flecto/translations'
import { type CoreBookingService, type ParameterID } from '@flecto/types'
import { type CoreBookingServicePayload } from '@flecto/types/src/core/CoreBookingServicePayload'
import {
  useNotifications,
  Modal,
  BadgeEditable,
  Icon,
  InputCurrency,
  TextCurrency,
} from '@flecto/ui'

import { InputTaxesSelector, useTaxesInfo } from '../../../taxes'
import { useServiceKind } from '../../hooks/useServiceKind'
import styles from './styles.module.css'

export type ModalBookingServiceProps = {
  bookingId?: ParameterID
  bookingService?: CoreBookingService
}

export function ModalBookingService({
  bookingId,
  bookingService,
}: ModalBookingServiceProps) {
  const { t } = useTranslation('saas', { keyPrefix: 'booking_services' })
  const { t: tActions } = useTranslation('shared', { keyPrefix: 'actions' })
  const { mutateAsync: updateBooking, isLoading: isUpdating } =
    useMutationUpdateBooking()

  const {
    getTaxBracket,
    getCalculatedAmounts,
    userDefaultTaxBracket,
    userSettingShowPricesWithTaxes,
  } = useTaxesInfo()

  const { showNotification } = useNotifications()

  const { data: booking } = useQueryGetBookingById({ bookingId })
  const { data: addresses } = useQueryGetUserAddresses()
  const { data: userData } = useQueryGetUserLogged()

  const { isFetching: isLoadingQuote, refetch } =
    useQueryGetAllBookingInsurancesQuote({
      bookingId,
      enabled: false,
    })

  const serviceKinds = useServiceKind()

  const isEndBeforeToday =
    booking?.start_at && booking.end_at
      ? isBefore(new Date(booking.end_at), new Date())
      : false

  const [inputValue, setInputValue] = useState(
    Number(bookingService?.price_cents ?? 0)
  )

  const [selected, setSelected] = useState<string | undefined>()

  const [localTaxable, setLocalTaxable] = useState(
    Boolean(bookingService?.taxable ?? true)
  )

  const [localTaxRate, setLocalTaxRate] = useState(
    String(bookingService?.tax_rate ?? userDefaultTaxBracket.value)
  )

  const [localPopoverTaxRate, setLocalPopoverTaxRate] = useState(
    String(bookingService?.tax_rate ?? userDefaultTaxBracket.value)
  )

  const [localPopoverTaxable, setLocalPopoverTaxable] = useState(
    Boolean(bookingService?.taxable ?? true)
  )

  const [localAmountTaxes, setLocalAmountTaxes] = useState(0)
  const [localAmountTotalWithoutTaxes, setLocalAmountTotalWithoutTaxes] =
    useState(0)
  const [localAmountTotalWithTaxes, setLocalAmountTotalWithTaxes] = useState(0)

  const taxBracket = getTaxBracket(localTaxRate)

  useEffect(() => {
    const legalAddress = addresses?.filter((address) => address.legal)
    if (
      legalAddress?.at(0)?.address_country_code === 'PT' &&
      selected === 'insurance' &&
      userData?.chargebee_subscription_plan_id !== 'flecto-starter'
    ) {
      setLocalPopoverTaxable(false)
      setLocalTaxRate('exempt')
      setLocalTaxable(false)
    } else if (selected !== 'insurance') {
      setLocalTaxable(true)
    }
  }, [addresses, selected, userData])

  useEffect(() => {
    setSelected(bookingService?.service_kind ?? 'others')

    setInputValue(bookingService?.price_cents ?? 0)
  }, [bookingService])

  const handleSave = async () => {
    const service = {
      price_cents: localAmountTotalWithoutTaxes,
      tax_rate: localTaxRate ?? '0',
      taxable: localTaxable,
      service_kind: selected ?? 'others',
    } as CoreBookingServicePayload

    if (bookingService) {
      service.id = bookingService.id
    }

    try {
      await updateBooking({
        bookingId,
        booking: { booking_services_attributes: [{ ...service }] },
      })

      showNotification({
        type: 'success',
        message: t('notification_modal_service_selection_success'),
      })
    } catch {
      showNotification({
        type: 'error',
        message: t('notification_modal_service_selection_error'),
      })
    }
  }

  const tooltipDescription = () => {
    return isEndBeforeToday
      ? t('estimate_insurance_tooltip_past')
      : t('estimate_insurance_tooltip')
  }

  const handleSetInsuranceEstimate = async () => {
    const { data } = await refetch()
    setInputValue(data?.total_cents ?? 0)
  }

  useEffect(() => {
    const amounts = getCalculatedAmounts(
      inputValue,
      localTaxRate,
      localTaxable,
      userSettingShowPricesWithTaxes ? false : true
    )

    setLocalAmountTaxes(amounts.taxes.cents)
    setLocalAmountTotalWithTaxes(amounts.totalWithTaxes.cents)
    setLocalAmountTotalWithoutTaxes(amounts.totalWithoutTaxes.cents)
  }, [inputValue, localTaxRate, localTaxable])

  const handleTaxPopoverConfirm = () => {
    setLocalTaxRate(localPopoverTaxRate)
    setLocalTaxable(localPopoverTaxable)
  }

  const handleTaxPopoverCancel = () => {
    setLocalPopoverTaxRate(localTaxRate)
    setLocalPopoverTaxable(localTaxable)
  }

  return (
    <Modal size='sm'>
      <Modal.Title>
        {t(bookingService ? 'label_update_service' : 'label_add_service')}
      </Modal.Title>
      <Modal.Content>
        <Radio.Group
          value={selected}
          onChange={setSelected}
          name='services'
          size='md'
          mb='md'
        >
          {serviceKinds.map((service) => (
            <Radio
              key={service.value}
              label={service.label}
              value={service.value}
              mb='sm'
            />
          ))}
        </Radio.Group>
        <Box mb='md'>
          <InputCurrency
            value={inputValue}
            label={t('label_price')}
            onChange={setInputValue}
          />
        </Box>
        {selected === 'insurance' && (
          <Box mb='md'>
            <Flex gap='xs' mb='sm'>
              <Text>{t('estimate_insurance_description')}</Text>
              <Tooltip
                label={tooltipDescription()}
                w={250}
                multiline
                withinPortal
              >
                <Box>
                  <Icon icon={faCircleQuestion} />
                </Box>
              </Tooltip>
            </Flex>
            <Button
              maw={230}
              size='sm'
              mb='md'
              variant='outline'
              leftSection={<Icon icon={faBadgeDollar} />}
              onClick={handleSetInsuranceEstimate}
              loading={isLoadingQuote}
              disabled={isEndBeforeToday}
            >
              {t('estimate_insurance_button')}
            </Button>
          </Box>
        )}

        <Flex justify='space-between' align='center' mb='sm' mt='md'>
          <Text data-testid='service-percent'>
            {t('label_amount_total_without_taxes')}
          </Text>
          <TextCurrency cents={localAmountTotalWithoutTaxes} />
        </Flex>

        <Flex justify='space-between' align='center' mb='sm'>
          <Text
            data-testid='service-percent'
            c={localTaxable ? undefined : 'dimmed'}
          >
            {t('label_amount_taxes', {
              replace: {
                count: localTaxable ? (taxBracket?.percentage ?? 0) : 0,
              },
            })}
          </Text>
          <BadgeEditable
            mr={-8}
            color='gray'
            value={<TextCurrency cents={localAmountTaxes} />}
            rightSection={<Icon icon={faEdit} size='xs' ml='xs' mr='xs' />}
            className={styles.taxesBadge}
            popoverProps={{
              position: 'top-end',
              withArrow: false,
            }}
          >
            <BadgeEditable.Dropdown>
              <Flex align='center' justify='space-between' gap='sm' mb='md'>
                <Text fw={500}>{t('label_apply_taxes')}</Text>
                <Switch
                  checked={localPopoverTaxable}
                  onChange={(e) =>
                    setLocalPopoverTaxable(e.currentTarget.checked)
                  }
                />
              </Flex>
              <InputTaxesSelector
                mb='sm'
                disabled={!localPopoverTaxable}
                onChange={(value: string | number) =>
                  setLocalPopoverTaxRate(String(value))
                }
                value={localPopoverTaxRate || ''}
              />
            </BadgeEditable.Dropdown>
            <BadgeEditable.Confirm onClick={() => handleTaxPopoverConfirm()} />
            <BadgeEditable.Close onClick={() => handleTaxPopoverCancel()} />
          </BadgeEditable>
        </Flex>

        <Flex justify='space-between' align='center' mb='sm'>
          <Text data-testid='service-percent'>
            {t('label_amount_total_with_taxes')}
          </Text>
          <TextCurrency cents={localAmountTotalWithTaxes} />
        </Flex>
      </Modal.Content>

      <Modal.Close />
      <Modal.Cancel />
      <Modal.Submit
        label={tActions('save')}
        disabled={!selected}
        onClick={handleSave}
        loading={isUpdating}
      />
    </Modal>
  )
}
